import { Dialog } from '@/components/dialog';
import { Skeleton } from '@/components/skeleton';
import { useMailDispatch } from '@/lib/email-composer';
import { formatDateTime } from '@/lib/format';
import { extractMessage } from '@/lib/form-error';

/**
 * A sent email exactly as it went out: the copy kept at send time, so it still
 * shows what the client read after the wording or template has changed.
 */
export function SentEmailDialog({ dispatchId, onClose }: { dispatchId: number; onClose: () => void }) {
  const dispatch = useMailDispatch(dispatchId);
  const email = dispatch.data;

  return (
    <Dialog open onClose={onClose} title={email?.subject ?? 'Sent email'} size="xl">
      {dispatch.isError ? (
        <p className="text-sm text-[var(--color-danger)]">{extractMessage(dispatch.error)}</p>
      ) : !email ? (
        <div className="space-y-3">
          <Skeleton className="h-5 w-2/3" />
          <Skeleton className="h-[50vh] w-full" />
        </div>
      ) : (
        <div className="space-y-3">
          <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
            <dt className="text-[var(--color-foreground-muted)]">To</dt>
            <dd className="break-all">{email.to.join(', ')}</dd>
            {email.cc.length > 0 ? (
              <>
                <dt className="text-[var(--color-foreground-muted)]">Cc</dt>
                <dd className="break-all">{email.cc.join(', ')}</dd>
              </>
            ) : null}
            <dt className="text-[var(--color-foreground-muted)]">Sent</dt>
            <dd>
              {formatDateTime(email.sent_at)}
              {email.sent_by ? ` by ${email.sent_by}` : ''}
            </dd>
          </dl>
          {email.html ? (
            <iframe
              title="Sent email"
              srcDoc={email.html}
              // Rendered inert: no scripts and no access to the app.
              sandbox=""
              className="h-[60vh] w-full rounded-lg border border-[var(--color-border)] bg-white"
            />
          ) : (
            <p className="rounded-lg border border-dashed border-[var(--color-border)] p-4 text-sm text-[var(--color-foreground-muted)]">
              No copy of this email was kept. Emails sent before November 2026, and a few automatic
              payment confirmations, don't have one.
            </p>
          )}
        </div>
      )}
    </Dialog>
  );
}
